import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { Mail, Trash2, Check, Inbox, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const MessagesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.patch(`/contact/${id}/read`);
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const unread = items.filter((m) => !m.is_read).length;

  return (
    <div data-testid="messages-panel">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ink">Messages</h2>
          <p className="text-sm text-smoke mt-1">
            {items.length} total &middot; {unread} unread
          </p>
        </div>
        <button
          onClick={load}
          data-testid="refresh-messages-btn"
          className="flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm text-smoke transition-colors hover:border-gold hover:text-gold"
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-smoke animate-pulse">Loading...</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line py-24 text-[#555]">
          <Inbox size={40} className="mb-4" />
          <p>No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((m) => (
            <div
              key={m.id}
              data-testid={`message-${m.id}`}
              className={`rounded-xl border bg-surface p-6 transition-colors ${
                m.is_read ? "border-line" : "border-gold/40"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold">
                    <Mail size={18} />
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink">{m.name}</span>
                      {!m.is_read && (
                        <span className="rounded-full bg-coral px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                          New
                        </span>
                      )}
                    </div>
                    <a href={`mailto:${m.email}`} className="text-sm text-smoke hover:text-gold">
                      {m.email}
                    </a>
                    {m.phone ? <span className="text-sm text-smoke"> &middot; {m.phone}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!m.is_read && (
                    <button
                      onClick={() => markRead(m.id)}
                      data-testid={`mark-read-${m.id}`}
                      title="Mark as read"
                      className="rounded-full p-2 text-smoke transition-colors hover:bg-gold/10 hover:text-gold"
                    >
                      <Check size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => remove(m.id)}
                    data-testid={`delete-message-${m.id}`}
                    title="Delete"
                    className="rounded-full p-2 text-smoke transition-colors hover:bg-coral/10 hover:text-coral"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <p className="mt-4 whitespace-pre-wrap text-[#D0D0D0] leading-relaxed">{m.message}</p>
              <p className="mt-3 text-xs text-[#555]">
                {new Date(m.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
